import {
  APIListValuesRequest,
  APIListValuesResponse,
  APIPaginatedRequest,
  AssociatedVehicleTimelineRequest,
  ByVehicleAgeAgeRequest,
  ExportRequest,
  getFetcher,
  ListEventsRequest,
  TopContributorsAgeRequest,
} from "shared/api/api";
import client from "shared/api/axios";
import { Granularity } from "shared/api/constants";
import { createURL } from "shared/api/utils";
import { CountRequestWithVehiclesFilter } from "shared/api/vehicles/api";
import { MileageUnit } from "shared/types";

const INSPECTIONS_BASE_ROUTE = "inspections";

export interface Inspection {
  ID: string;
  VIN: string;
  createdAt: string;
  updatedAt: string;
  date: string;
  data: Record<string, string | undefined>;
  externalID: string | null;
  externalURL: string | null;
  notes: string | null;
  status: string | null;
  mileage: number | null;
  mileageUnit: MileageUnit | null;
  monthsInService: number | null;
  daysInService: number | null;
  totalEngineHours: number | null;
  items: InspectionItem[];
}

export interface InspectionItem {
  item: string;
  createdAt: string;
  updatedAt: string;
  desciption: string | null;
  passed: boolean | null;
  notes: string | null;
  fixedAt: string | null;
}

// List & count
export const listInspectionsRequestURI = ({
  skipRequest,
  ...params
}: ListEventsRequest): string =>
  skipRequest
    ? ""
    : client.getUri({
        method: "GET",
        url: INSPECTIONS_BASE_ROUTE,
        params,
      });

export const listInspections = (args: APIPaginatedRequest) =>
  getFetcher(listInspectionsRequestURI({ ...args, analytics: true }));

export const listInspectionsCountRequestURI = ({
  ...params
}: CountRequestWithVehiclesFilter): string =>
  client.getUri({
    method: "GET",
    url: createURL([INSPECTIONS_BASE_ROUTE, "count"]),
    params,
  });

// Inspections values
export const listInspectionsValues = (args: APIListValuesRequest) =>
  getFetcher<APIListValuesResponse>(listInspectionsValuesRequestURI(args));

const listInspectionsValuesRequestURI = ({
  fieldName,
  ...params
}: APIListValuesRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "values", fieldName]),
    params,
  });

// Export
const getInspectionsExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "export", type]),
    params,
  });

export const getInspectionsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getInspectionsExportRequestURI(args), {
    responseType: "blob",
  });

export interface InspectionFiltersRequest {
  inspectionsFilter?: string;
  vehiclesFilter?: string;
  signalEventOccurrencesFilter?: string;
}

// Run Chart
export interface InspectionsRunChartRequest extends InspectionFiltersRequest {
  groupBy: string | null;
  numSplits?: number;
  granularity?: Granularity;
}

export interface InspectionsRunChartResponse {
  data: InspectionsRunChartItem[];
  numTotalSplits: number | null;
}

export interface InspectionsRunChartItem {
  date: string;
  affectedVehicles: number;
  events: number;
  groupByAttributeValue: string | null;
}

export const getInspectionsRunChartRequestURI = (
  params: InspectionsRunChartRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "runChart"]),
    params,
  });

// Single inspection
export interface GetInspectionRequest {
  ID: string;
  mileageUnit: MileageUnit;
}

export const getInspectionRequestURI = ({
  ID,
  ...params
}: GetInspectionRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, ID]),
    params,
  });

export const getInspection = (args: GetInspectionRequest) =>
  getFetcher<Inspection>(getInspectionRequestURI(args));

// Top Contributors
export interface InspectionsTopContributorsRequest
  extends
    APIPaginatedRequest,
    InspectionFiltersRequest,
    TopContributorsAgeRequest {
  groupByAttribute: string;
}

export interface InspectionsTopContributor {
  groupByAttributeValue: string | number | boolean | null;
  IPTV: number;
  IPTV30DayChangeRatio: number | null;
  DPTV: number;
  DPTV30DayChangeRatio: number | null;
  numEvents: number;
  numVehicles: number;
  exposureBucket: number | null;
}

export const getInspectionsTopContributorsRequestURI = ({
  groupByAttribute,
  ...otherParams
}: InspectionsTopContributorsRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      INSPECTIONS_BASE_ROUTE,
      "topContributors",
      groupByAttribute,
    ]),
    params: otherParams,
  });

const getInspectionsTopContributorsExportRequestURI = ({
  IDs,
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      INSPECTIONS_BASE_ROUTE,
      "topContributors",
      ...IDs,
      "export",
      type,
    ]),
    params,
  });

export const getInspectionsTopContributorsExport = (args: ExportRequest) =>
  getFetcher<Blob>(getInspectionsTopContributorsExportRequestURI(args), {
    responseType: "blob",
  });

// Associated Vehicles
export interface InspectionAssociatedVehiclesRequest
  extends APIPaginatedRequest, InspectionFiltersRequest {}

export const listAssociatedVehiclesRequestURI = (
  params: InspectionAssociatedVehiclesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "associatedVehicles"]),
    params,
  });

export const countAssociatedVehiclesRequestURI = (
  params: InspectionAssociatedVehiclesRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "associatedVehicles", "count"]),
    params,
  });

const getAssociatedVehiclesExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      INSPECTIONS_BASE_ROUTE,
      "associatedVehicles",
      "export",
      type,
    ]),
    params,
  });

export const getInspectionsAssociatedVehiclesExport = (args: ExportRequest) =>
  getFetcher<Blob>(getAssociatedVehiclesExportRequestURI(args), {
    responseType: "blob",
  });

// By Production Date
export interface InspectionsByVehicleAgeAgeRequest
  extends InspectionFiltersRequest, ByVehicleAgeAgeRequest {}

export const getInspectionsByVehicleAgeTimelineRequestURI = (
  params: InspectionFiltersRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "inspectionsByVehicleAgeTimeline"]),
    params,
  });

const getInspectionsByVehicleAgeTimelineExportRequestURI = ({
  type = "xlsx",
  ...params
}: ExportRequest): string =>
  client.getUri({
    method: "get",
    url: createURL([
      INSPECTIONS_BASE_ROUTE,
      "inspectionsByVehicleAgeTimeline",
      "export",
      type,
    ]),
    params,
  });

export const getInspectionsByVehicleAgeTimelineExport = (args: ExportRequest) =>
  getFetcher<Blob>(getInspectionsByVehicleAgeTimelineExportRequestURI(args), {
    responseType: "blob",
  });

// Associated signal events
export interface InspectionAssociatedSignalEvent {
  IPTV: number;
  numAssociatedInspections: number;
  numAssociatedVehicles: number;
  percentInspectionsWithPreceding30DaysSignalEvent: number;
  signalEventDescription: string | null;
  signalEventID: string;
  totalEventOccurrences: number;
  associationStrength: number | null;
}

export interface InspectionAssociatedSignalEventsRequest
  extends APIPaginatedRequest, InspectionFiltersRequest {
  signalEventsTimeWindow?: number;
}

export const listAssociatedSignalEventsRequestURI = (
  params: InspectionAssociatedSignalEventsRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "associatedSignalEvents"]),
    params,
  });

export const listAssociatedVehiclesTimelineRequestURI = (
  params: AssociatedVehicleTimelineRequest
): string =>
  client.getUri({
    method: "get",
    url: createURL([INSPECTIONS_BASE_ROUTE, "associatedVehiclesTimeline"]),
    params,
  });
